import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/context/AuthContext";
import { userApi } from "@/services/userService";
import { projectApi } from "@/services/projectService";
import { unifiedInvitationService } from "@/services/unifiedInvitationService";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { Loader2, UserMinus, UserPlus, Trash2, Plus } from "lucide-react";
import { Roles } from "@/enums/roles.enum";

interface UserDetailsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userId: string;
}

interface ProjectMembership {
  _id: string;
  name: string;
  role?: string;
}

export const UserDetailsModal = ({
  open,
  onOpenChange,
  userId,
}: UserDetailsModalProps) => {
  const { user: currentUser } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [selectedProject, setSelectedProject] = useState<string>("");
  const [selectedRole, setSelectedRole] = useState<Roles>(Roles.CLIENT);

  // Fetch user details
  const { data: userData, isLoading: userLoading } = useQuery({
    queryKey: ["user", userId],
    queryFn: async () => {
      const users = await userApi.getUsers();
      return users.users.find((u) => u._id === userId);
    },
    enabled: open && !!userId,
  });

  // Fetch all projects to show available projects for invitation
  const { data: projectsData, isLoading: projectsLoading } = useQuery({
    queryKey: ["projects"],
    queryFn: () => projectApi.getProjects(),
    enabled: open,
  });

  // Get user's current project memberships
  const userProjects =
    projectsData?.projects?.filter(
      (project) =>
        project.members?.some((member) => member._id === userId) ||
        project.createdBy._id === userId
    ) || [];

  // Get available projects for invitation (projects user is not part of)
  const availableProjects =
    projectsData?.projects?.filter(
      (project) =>
        !project.members?.some((member) => member._id === userId) &&
        project.createdBy._id !== userId
    ) || [];

  // Remove user from project mutation
  const removeFromProjectMutation = useMutation({
    mutationFn: ({ projectId }: { projectId: string }) =>
      projectApi.removeMemeberFromProject(projectId, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["projects"] });
      toast({
        title: "Success",
        description: "User removed from project successfully.",
      });
    },
    onError: (error: unknown) => {
      const errorMessage =
        error &&
        typeof error === "object" &&
        "response" in error &&
        error.response &&
        typeof error.response === "object" &&
        "data" in error.response &&
        error.response.data &&
        typeof error.response.data === "object" &&
        "error" in error.response.data
          ? String((error.response.data as { error: string }).error)
          : "Failed to remove user from project";
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    },
  });

  // Invite user to project mutation
  const inviteToProjectMutation = useMutation({
    mutationFn: ({ projectId, role }: { projectId: string; role: Roles }) =>
      unifiedInvitationService.inviteUserToProject(projectId, {
        email: userData?.email || "",
        role,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["projects"] });
      setSelectedProject("");
      setSelectedRole(Roles.CLIENT);
      toast({
        title: "Success",
        description: "User invited to project successfully.",
      });
    },
    onError: (error: unknown) => {
      const errorMessage =
        error &&
        typeof error === "object" &&
        "response" in error &&
        error.response &&
        typeof error.response === "object" &&
        "data" in error.response &&
        error.response.data &&
        typeof error.response.data === "object" &&
        "error" in error.response.data
          ? String((error.response.data as { error: string }).error)
          : "Failed to invite user to project";
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    },
  });

  const getRoleBadgeVariant = (role: string) => {
    switch (role) {
      case Roles.ADMIN:
        return "destructive";
      case Roles.VENDOR:
        return "default";
      case Roles.CONTRACTOR:
        return "secondary";
      case Roles.CLIENT:
      default:
        return "outline";
    }
  };

  const canRemoveFromProject = (project: { createdBy: { _id: string } }) => {
    if (!currentUser) return false;
    if (currentUser.role === Roles.ADMIN) return true;
    if (project.createdBy._id === currentUser._id) return true;
    return false;
  };

  const canInviteToProject = () => {
    if (!currentUser) return false;
    return [Roles.ADMIN, Roles.VENDOR].includes(currentUser.role);
  };

  const handleRemoveFromProject = (projectId: string) => {
    removeFromProjectMutation.mutate({ projectId });
  };

  const handleInviteToProject = () => {
    if (selectedProject && selectedRole) {
      inviteToProjectMutation.mutate({
        projectId: selectedProject,
        role: selectedRole,
      });
    }
  };

  if (!userData && !userLoading) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>User Management</DialogTitle>
          <DialogDescription>
            Manage user project memberships and permissions
          </DialogDescription>
        </DialogHeader>

        {userLoading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        ) : userData ? (
          <div className="space-y-6">
            {/* User Information */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">User Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">
                      Name
                    </p>
                    <p className="font-medium">
                      {`${userData.firstName} ${userData.lastName}`.trim()}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">
                      Email
                    </p>
                    <p className="font-medium">{userData.email}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">
                      Role
                    </p>
                    <Badge variant={getRoleBadgeVariant(userData.role)}>
                      {userData.role}
                    </Badge>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">
                      Company
                    </p>
                    <p className="font-medium">{userData.companyName || "—"}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Current Project Memberships */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Current Projects</CardTitle>
                <p className="text-sm text-muted-foreground">
                  Projects this user is currently a member of
                </p>
              </CardHeader>
              <CardContent>
                {userProjects.length === 0 ? (
                  <p className="text-muted-foreground text-center py-4">
                    User is not a member of any projects
                  </p>
                ) : (
                  <div className="space-y-3">
                    {userProjects.map((project) => (
                      <div
                        key={project._id}
                        className="flex items-center justify-between p-3 border rounded-lg"
                      >
                        <div className="flex-1">
                          <h4 className="font-medium">{project.name}</h4>
                          <p className="text-sm text-muted-foreground">
                            {project.createdBy._id === userId
                              ? "Project Owner"
                              : "Member"}
                          </p>
                        </div>
                        {canRemoveFromProject(project) &&
                          project.createdBy._id !== userId && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() =>
                                handleRemoveFromProject(project._id)
                              }
                              disabled={removeFromProjectMutation.isPending}
                            >
                              {removeFromProjectMutation.isPending ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <UserMinus className="h-4 w-4" />
                              )}
                              Remove
                            </Button>
                          )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Add to Project */}
            {canInviteToProject() && availableProjects.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Add to Project</CardTitle>
                  <p className="text-sm text-muted-foreground">
                    Invite this user to join a new project
                  </p>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium">Project</label>
                      <Select
                        value={selectedProject}
                        onValueChange={setSelectedProject}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select project" />
                        </SelectTrigger>
                        <SelectContent>
                          {availableProjects.map((project) => (
                            <SelectItem key={project._id} value={project._id}>
                              {project.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-sm font-medium">Role</label>
                      <Select
                        value={selectedRole}
                        onValueChange={(e) => setSelectedRole(e as Roles)}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value={Roles.CLIENT}>Client</SelectItem>
                          <SelectItem value={Roles.CONTRACTOR}>
                            Contractor
                          </SelectItem>
                          <SelectItem value={Roles.VENDOR}>Vendor</SelectItem>
                          <SelectItem value={Roles.ADMIN}>Admin</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <Button
                    onClick={handleInviteToProject}
                    disabled={
                      !selectedProject || inviteToProjectMutation.isPending
                    }
                    className="w-full"
                  >
                    {inviteToProjectMutation.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <Plus className="h-4 w-4 mr-2" />
                    )}
                    Invite to Project
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        ) : null}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
